/*

 *

 * Author: Codex (GPT-5.1)

 * Date: 2025-12-18

 * PURPOSE: Centralized AI model configuration list consumed by ModelDefinitions and provider lookup utilities.

 *          Added Gemini 3 Flash Preview metadata for both the native and OpenRouter hierarchies so the low-latency reasoning tier is available across the ARC pipeline.

 * SRP/DRY check: Pass - file encapsulates shared model metadata without duplication.

 * shadcn/ui: Pass - configuration only.

 */

import type { ModelConfig } from '@shared/types';
import { buildOpenRouterModels } from './openrouterModels.js';


const STATIC_MODELS: ModelConfig[] = [
  // OpenAI Models
  { 
    key: 'gpt-4.1-nano-2025-04-14', 
    name: 'GPT-4.1 Nano', 
    color: 'bg-blue-500', 
    premium: false,
    cost: { input: '$0.10', output: '$0.40' },
    supportsTemperature: true,
    provider: 'OpenAI',
    responseTime: { speed: 'fast', estimate: '<30 sec' },
    apiModelName: 'gpt-4.1-nano-2025-04-14',
    modelType: 'gpt5_chat',
    contextWindow: 1000000,
    releaseDate: "2025-04"
  },
  { 
    key: 'gpt-4.1-mini-2025-04-14', 
    name: 'GPT-4.1 Mini', 
    color: 'bg-purple-500', 
    premium: false,
    cost: { input: '$0.40', output: '$1.60' },
    supportsTemperature: true,
    provider: 'OpenAI',
    responseTime: { speed: 'moderate', estimate: '30-60 sec' },
    apiModelName: 'gpt-4.1-mini-2025-04-14',
    modelType: 'gpt5_chat',
    contextWindow: 1000000,
    releaseDate: "2025-04"
  },
  { 
    key: 'gpt-4o-mini-2024-07-18', 
    name: 'GPT-4o Mini', 
    color: 'bg-orange-500', 
    premium: false,
    cost: { input: '$0.15', output: '$0.60' },
    supportsTemperature: true,
    provider: 'OpenAI',
    responseTime: { speed: 'moderate', estimate: '30-60 sec' },
    apiModelName: 'gpt-4o-mini-2024-07-18',
    modelType: 'gpt5_chat',
    contextWindow: 128000,
    releaseDate: "2024-07"
  },
  { 
    key: 'o3-mini-2025-01-31', 
    name: 'o3-mini', 
    color: 'bg-red-500', 
    premium: true,
    cost: { input: '$1.10', output: '$4.40' },
    supportsTemperature: false,
    supportsStreaming: true,
    provider: 'OpenAI',
    responseTime: { speed: 'moderate', estimate: '1-2 min' },
    isReasoning: true,
    apiModelName: 'o3-mini-2025-01-31',
    modelType: 'o3_o4',
    contextWindow: 200000,
    releaseDate: "2025-01"
  },
  { 
    key: 'o4-mini-2025-04-16', 
    name: 'o4-mini', 
    color: 'bg-pink-500', 
    premium: true,
    cost: { input: '$1.10', output: '$4.40' },
    supportsTemperature: false,
    supportsStreaming: true,
    provider: 'OpenAI',
    responseTime: { speed: 'moderate', estimate: '1-3 min' },
    isReasoning: true,
    apiModelName: 'o4-mini-2025-04-16',
    modelType: 'o3_o4',
    contextWindow: 200000,
    releaseDate: "2025-04"
  },
  { 
    key: 'o3-2025-04-16', 
    name: 'o3-2025-04-16', 
    color: 'bg-green-500', 
    premium: true,
    cost: { input: '$2', output: '$8' },
    supportsTemperature: false,
    supportsStreaming: true,
    provider: 'OpenAI',
    responseTime: { speed: 'slow', estimate: '3-5+ min' },
    isReasoning: true,
    apiModelName: 'o3-2025-04-16',
    modelType: 'o3_o4',
    contextWindow: 400000,
    releaseDate: "2025-04"
  },
  { 
    key: 'gpt-4.1-2025-04-14', 
    name: 'GPT-4.1', 
    color: 'bg-yellow-500', 
    premium: true,
    cost: { input: '$2.00', output: '$8.00' },
    supportsTemperature: true,
    provider: 'OpenAI',
    responseTime: { speed: 'moderate', estimate: '1-2 min' },
    apiModelName: 'gpt-4.1-2025-04-14',
    modelType: 'gpt5_chat',
    contextWindow: 1000000,
    releaseDate: "2025-04"
  },
  { 
    key: 'gpt-5-2025-08-07', 
    name: 'GPT-5', 
    color: 'bg-emerald-500', 
    premium: true,
    cost: { input: '$1.25', output: '$10.00' },
    supportsTemperature: false,
    supportsStreaming: true,
    supportsVision: true,
    provider: 'OpenAI',
    responseTime: { speed: 'moderate', estimate: '1-2 min' },
    isReasoning: true,
    apiModelName: 'gpt-5-2025-08-07',
    modelType: 'gpt5',
    contextWindow: 400000,
    releaseDate: "2025-08"
  },
  { 
    key: 'gpt-5-chat-latest', 
    name: 'GPT-5 Chat', 
    color: 'bg-amber-500', 
    premium: false,
    cost: { input: '$1.25', output: '$10.00' },
    supportsTemperature: false,
    supportsStreaming: true,
    supportsVision: true,
    provider: 'OpenAI',
    responseTime: { speed: 'fast', estimate: '<30 sec' },
    isReasoning: false,
    apiModelName: 'gpt-5-chat-latest',
    modelType: 'gpt5_chat',
    contextWindow: 256000,
    releaseDate: "2025-08"
  },
  { 
    key: 'gpt-5-mini-2025-08-07', 
    name: 'GPT-5 Mini', 
    color: 'bg-indigo-500', 
    premium: true,
    cost: { input: '$0.25', output: '$2.00' },
    supportsTemperature: false,
    supportsStreaming: true,
    supportsVision: true,
    provider: 'OpenAI',
    responseTime: { speed: 'moderate', estimate: '30-60 sec' },
    isReasoning: true,
    apiModelName: 'gpt-5-mini-2025-08-07',
    modelType: 'gpt5',
    contextWindow: 400000,
    releaseDate: "2025-08"
  },
  { 
    key: 'gpt-5-nano-2025-08-07', 
    name: 'GPT-5 Nano', 
    color: 'bg-teal-500', 
    premium: false,
    cost: { input: '$0.05', output: '$0.40' },
    supportsTemperature: false,
    supportsStreaming: true,
    supportsVision: true,
    provider: 'OpenAI',
    responseTime: { speed: 'fast', estimate: '<30 sec' },
    isReasoning: true,
    apiModelName: 'gpt-5-nano-2025-08-07',
    modelType: 'gpt5',
    contextWindow: 400000,
    releaseDate: "2025-08"
  },
  { 
    key: 'gpt-5.1-codex-mini', 
    name: 'GPT-5.1 Codex Mini', 
    color: 'bg-amber-600', 
    premium: true,
    cost: { input: '$0.25', output: '$2.00' },
    supportsTemperature: false,
    supportsStreaming: true,
    supportsVision: true,
    provider: 'OpenAI',
    responseTime: { speed: 'moderate', estimate: '30-60 sec' },
    isReasoning: true,
    apiModelName: 'gpt-5.1-codex-mini',
    modelType: 'gpt5',
    contextWindow: 400000,
    releaseDate: "2025-11",
    notes: 'Sep 30, 2024 knowledge cutoff; reasoning token support.'
  },
  {
    key: 'gpt-5.1-codex',
    name: 'GPT-5.1 Codex',
    color: 'bg-amber-600',
    premium: true,
    cost: { input: '$1.25', output: '$10.00' },
    supportsTemperature: false,
    supportsStreaming: true,
    supportsVision: true,
    provider: 'OpenAI',
    responseTime: { speed: 'moderate', estimate: '30-60 sec' },
    isReasoning: true,
    apiModelName: 'gpt-5.1-codex',
    modelType: 'gpt5',
    contextWindow: 400000,
    releaseDate: "2025-11",
    notes: 'Sep 30, 2024 knowledge cutoff; reasoning token support.'
  },
  {
    key: 'gpt-5.1-2025-11-13',
    name: 'GPT-5.1',
    color: 'bg-amber-700',
    premium: true,
    cost: { input: '$1.25', output: '$10.00' },
    supportsTemperature: false,
    supportsStreaming: true,
    supportsVision: true,
    provider: 'OpenAI',
    responseTime: { speed: 'moderate', estimate: '1-2 min' },
    isReasoning: true,
    apiModelName: 'gpt-5.1-2025-11-13',
    modelType: 'gpt5',
    contextWindow: 400000,
    maxOutputTokens: 128000,
    releaseDate: "2025-11",
    notes: 'GPT-5.1 base model released November 13, 2025.'
  },

  // Anthropic Models
  {
    key: 'claude-opus-4-6',
    name: 'Claude Opus 4.6',
    color: 'bg-indigo-900',
    premium: true,
    cost: { input: '$5.00', output: '$25.00' },
    supportsTemperature: true,
    provider: 'Anthropic',
    responseTime: { speed: 'slow', estimate: '2-5 min' },
    isReasoning: true,
    apiModelName: 'claude-opus-4-6',
    modelType: 'claude',
    contextWindow: 200000,
    maxOutputTokens: 32000,
    releaseDate: "2026-03",
    notes: 'EXPENSIVE: $5/M input, $25/M output. Use sparingly for benchmarking only.'
  },
  {
    key: 'claude-sonnet-4-6',
    name: 'Claude Sonnet 4.6',
    color: 'bg-indigo-600',
    premium: true,
    cost: { input: '$3.00', output: '$15.00' },
    supportsTemperature: true,
    provider: 'Anthropic',
    responseTime: { speed: 'moderate', estimate: '1-3 min' },
    isReasoning: true,
    apiModelName: 'claude-sonnet-4-6',
    modelType: 'claude',
    contextWindow: 200000,
    maxOutputTokens: 64000,
    releaseDate: "2026-03",
    notes: 'Current production Sonnet. Recommended for ARC-AGI-3 benchmarking.'
  },
  {
    key: 'claude-haiku-4-6',
    name: 'Claude Haiku 4.6',
    color: 'bg-indigo-300',
    premium: false,
    cost: { input: '$1.00', output: '$5.00' },
    supportsTemperature: true,
    provider: 'Anthropic',
    responseTime: { speed: 'fast', estimate: '<30 sec' },
    isReasoning: false,
    apiModelName: 'claude-haiku-4-6',
    modelType: 'claude',
    contextWindow: 200000,
    maxOutputTokens: 16000,
    releaseDate: "2026-03",
    notes: 'Current production Haiku. Recommended default for ARC-AGI-3 high-volume runs.'
  },
  {
    key: 'claude-sonnet-4-20250514',
    name: 'Claude Sonnet 4',
    color: 'bg-indigo-500',
    premium: true,
    cost: { input: '$3.00', output: '$15.00' },
    supportsTemperature: true,
    provider: 'Anthropic',
    responseTime: { speed: 'moderate', estimate: '1-3 min' },
    isReasoning: true,
    apiModelName: 'claude-sonnet-4-20250514',
    modelType: 'claude',
    maxOutputTokens: 64000,
    releaseDate: "2025-05"
  },
  {
    key: 'claude-sonnet-4-5-20250929',
    name: 'Claude Sonnet 4.5',
    color: 'bg-indigo-600',
    premium: true,
    cost: { input: '$3.00', output: '$15.00' },
    supportsTemperature: true,
    provider: 'Anthropic',
    responseTime: { speed: 'moderate', estimate: '1-3 min' },
    isReasoning: true,
    apiModelName: 'claude-sonnet-4-5-20250929',
    modelType: 'claude',
    maxOutputTokens: 64000,
    releaseDate: "2025-09"
  },
  {
    key: 'anthropic/claude-opus-4.5',
    name: 'Claude Opus 4.5',
    color: 'bg-indigo-900',
    premium: true,
    cost: { input: '$5.00', output: '$25.00' },
    supportsTemperature: true,
    provider: 'Anthropic',
    responseTime: { speed: 'slow', estimate: '2-5 min' },
    isReasoning: true,
    apiModelName: 'claude-opus-4.5-20251124',
    modelType: 'claude',
    contextWindow: 200000,
    maxOutputTokens: 16000,
    releaseDate: "2025-11",
    notes: 'EXTREMELY EXPENSIVE: $5/M input, $25/M output. Use sparingly for benchmarking only. High reasoning capability but significant cost implications. Each game match can cost $0.50-$5.00+ depending on token usage.'
  },
  { 
    key: 'claude-3-7-sonnet-20250219', 
    name: 'Claude 3.7 Sonnet', 
    color: 'bg-indigo-400', 
    premium: false,
    cost: { input: '$3.00', output: '$15.00' },
    supportsTemperature: true,
    provider: 'Anthropic',
    responseTime: { speed: 'moderate', estimate: '1-2 min' },
    isReasoning: true,
    apiModelName: 'claude-3-7-sonnet-20250219',
    modelType: 'claude',
    maxOutputTokens: 20000,
    releaseDate: "2025-02"
  },
  { 
    key: 'claude-3-5-sonnet-20241022', 
    name: 'Claude 3.5 Sonnet', 
    color: 'bg-violet-500', 
    premium: false,
    cost: { input: '$3.00', output: '$15.00' },
    supportsTemperature: true,
    provider: 'Anthropic',
    responseTime: { speed: 'moderate', estimate: '1-2 min' },
    isReasoning: true,
    apiModelName: 'claude-3-5-sonnet-20241022',
    modelType: 'claude',
    maxOutputTokens: 8192,
    releaseDate: "2024-10"
  },
  { 
    key: 'claude-3-5-haiku-20241022', 
    name: 'Claude 3.5 Haiku', 
    color: 'bg-violet-400', 
    premium: false,
    cost: { input: '$0.80', output: '$4.00' },
    supportsTemperature: true,
    provider: 'Anthropic',
    responseTime: { speed: 'moderate', estimate: '30-60 sec' },
    isReasoning: true,
    apiModelName: 'claude-3-5-haiku-20241022',
    modelType: 'claude',
    maxOutputTokens: 4000,
    releaseDate: "2024-10"
  },
  { 
    key: 'claude-haiku-4-5-20251015', 
    name: 'Claude Haiku 4.5', 
    color: 'bg-indigo-300', 
    premium: false,
    cost: { input: '$1.00', output: '$5.00' },
    supportsTemperature: true,
    provider: 'Anthropic',
    responseTime: { speed: 'fast', estimate: '<30 sec' },
    isReasoning: true,
    apiModelName: 'claude-haiku-4-5-20251015',
    modelType: 'claude',
    contextWindow: 200000,
    maxOutputTokens: 16000,
    releaseDate: "2025-10"
  },
  { 
    key: 'claude-3-haiku-20240307', 
    name: 'Claude 3 Haiku', 
    color: 'bg-purple-400', 
    premium: false,
    cost: { input: '$0.25', output: '$1.25' },
    supportsTemperature: true,
    provider: 'Anthropic',
    responseTime: { speed: 'fast', estimate: '<60 sec' },
    isReasoning: true,
    apiModelName: 'claude-3-haiku-20240307',
    modelType: 'claude',
    maxOutputTokens: 4000,
    releaseDate: "2024-03"
  },

  
  {
    key: 'gemini-3-pro-preview',
    name: 'Gemini 3 Pro Preview',
    color: 'bg-teal-700',
    premium: true,
    cost: { input: '$2.00 - $4.00', output: '$12.00 - $18.00' },
    supportsTemperature: true,
    provider: 'Gemini',
    responseTime: { speed: 'moderate', estimate: '1-2 min' },
    isReasoning: true,
    apiModelName: 'models/gemini-3-pro-preview',
    modelType: 'gemini',
    contextWindow: 1048576,
    maxOutputTokens: 65536,
    releaseDate: "2025-11",
    supportsStreaming: false,
    supportsStructuredOutput: false,
    supportsVision: true,
    notes: 'Direct Gemini API (preview). Keep JSON-mode disabled to preserve reasoning traces.'
  },
  // Gemini 3 Flash Preview surfaces the faster reasoning tier so ARC runs can pick the lower-latency release.
  {
    key: 'gemini-3-flash-preview',
    name: 'Gemini 3 Flash Preview',
    color: 'bg-teal-500',
    premium: false,
    cost: { input: '$0.50', output: '$3.00' },
    supportsTemperature: true,
    provider: 'Gemini',
    responseTime: { speed: 'fast', estimate: '<30 sec' },
    isReasoning: true,
    apiModelName: 'models/gemini-3-flash-preview',
    modelType: 'gemini',
    contextWindow: 1048576,
    maxOutputTokens: 65535,
    releaseDate: "2025-12",
    supportsVision: true,
    notes: 'Flash preview tuned for interactive agent loops; use the OpenRouter alias for the same tier via BYO routing.'
  },
  {
    key: 'gemini-2.5-pro',
    name: 'Gemini 2.5 Pro',
    color: 'bg-teal-600',
    premium: true,
    cost: { input: '$2.50', output: '$8.00' },
    supportsTemperature: true,
    provider: 'Gemini',
    responseTime: { speed: 'moderate', estimate: '1-2 min' },
    isReasoning: true,
    apiModelName: 'models/gemini-2.5-pro',
    modelType: 'gemini',
    contextWindow: 1000000,
    releaseDate: "2025-08"
  },
  {
    key: 'gemini-2.5-flash',
    name: 'Gemini 2.5 Flash',
    color: 'bg-teal-500',
    premium: false,
    cost: { input: '$0.70', output: '$2.10' },
    supportsTemperature: true,
    provider: 'Gemini',
    responseTime: { speed: 'moderate', estimate: '30-60 sec' },
    isReasoning: true,
    apiModelName: 'models/gemini-2.5-flash',
    modelType: 'gemini',
    contextWindow: 1000000,
    releaseDate: "2025-08"
  },
  {
    key: 'gemini-2.5-flash-lite',
    name: 'Gemini 2.5 Flash-Lite',
    color: 'bg-teal-400',
    premium: false,
    cost: { input: '$0.35', output: '$1.05' },
    supportsTemperature: true,
    provider: 'Gemini',
    responseTime: { speed: 'fast', estimate: '<30 sec' },
    isReasoning: true,
    apiModelName: 'models/gemini-2.5-flash-lite',
    modelType: 'gemini',
    contextWindow: 1000000,
    releaseDate: "2025-08"
  },
  {
    key: 'gemini-2.0-flash',
    name: 'Gemini 2.0 Flash',
    color: 'bg-teal-300',
    premium: false,
    cost: { input: '$0.20', output: '$0.60' },
    supportsTemperature: true,
    provider: 'Gemini',
    responseTime: { speed: 'fast', estimate: '<30 sec' },
    isReasoning: true,
    apiModelName: 'models/gemini-2.0-flash',
    modelType: 'gemini',
    contextWindow: 1000000,
    releaseDate: "2025-08"
  },
  {
    key: 'gemini-2.0-flash-lite',
    name: 'Gemini 2.0 Flash-Lite',
    color: 'bg-teal-200',
    premium: false,
    cost: { input: '$0.10', output: '$0.30' },
    supportsTemperature: true,
    provider: 'Gemini',
    responseTime: { speed: 'fast', estimate: '<30 sec' },
    isReasoning: true,
    apiModelName: 'models/gemini-2.0-flash-lite',
    modelType: 'gemini',
    contextWindow: 1000000,
    releaseDate: "2025-08"
  },
  {
    key: 'google/gemini-2.5-flash-lite-preview-09-2025',
    name: 'Gemini 2.5 Flash-Lite Preview (Sep 2025)',
    color: 'bg-teal-300',
    premium: false,
    cost: { input: '$0.10', output: '$0.40' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'fast', estimate: '<30 sec' },
    isReasoning: true,
    apiModelName: 'google/gemini-2.5-flash-lite-preview-09-2025',
    modelType: 'openrouter',
    contextWindow: 1050000,
    releaseDate: "2025-09"
  },
  {
    key: 'google/gemma-3n-e2b-it:free',
    name: 'Gemma 3N E2B (Free)',
    color: 'bg-teal-100',
    premium: false,
    cost: { input: '$0.00', output: '$0.00' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '30-60 sec' },
    isReasoning: false,
    apiModelName: 'google/gemma-3n-e2b-it:free',
    modelType: 'openrouter',
    contextWindow: 4000,
    releaseDate: "2025-12"
  },

  // DeepSeek Models
  {
    key: 'deepseek-chat',
    name: 'DeepSeek Chat v3.2',
    color: 'bg-cyan-600',
    premium: false,
    cost: { input: '$0.28', output: '$0.42' },
    supportsTemperature: true,
    provider: 'DeepSeek',
    responseTime: { speed: 'moderate', estimate: '30-90 sec' },
    isReasoning: false,
    apiModelName: 'deepseek-chat',
    modelType: 'deepseek',
    contextWindow: 128000,
    maxOutputTokens: 8000,
    releaseDate: "2025-12",
    notes: 'DeepSeek-V3.2 non-thinking mode. Supports JSON output, tool calls, chat prefix completion, and FIM completion. Cache hit: $0.028/1M input tokens.'
  },
  {
    key: 'deepseek-reasoner',
    name: 'DeepSeek Reasoner v3.2',
    color: 'bg-cyan-800',
    premium: true,
    cost: { input: '$0.28', output: '$0.42' },
    supportsTemperature: false,
    provider: 'DeepSeek',
    responseTime: { speed: 'slow', estimate: '5-10 min' },
    isReasoning: true,
    apiModelName: 'deepseek-reasoner',
    modelType: 'deepseek',
    contextWindow: 128000,
    maxOutputTokens: 64000,
    releaseDate: "2025-12",
    notes: 'DeepSeek-V3.2 thinking mode. Returns reasoning_content field with chain-of-thought. Temperature ignored. Cache hit: $0.028/1M input tokens.'
  },
  {
    key: 'deepseek-reasoner-speciale',
    name: 'DeepSeek Reasoner v3.2-Speciale',
    color: 'bg-cyan-900',
    premium: true,
    cost: { input: '$0.28', output: '$0.42' },
    supportsTemperature: false,
    provider: 'DeepSeek',
    responseTime: { speed: 'slow', estimate: '5-10 min' },
    isReasoning: true,
    apiModelName: 'deepseek-reasoner',
    modelType: 'deepseek',
    contextWindow: 128000,
    maxOutputTokens: 128000,
    releaseDate: "2025-12",
    notes: 'DeepSeek-V3.2-Speciale thinking mode. Special base URL. No JSON output, no tool calls. Available until December 15, 2025, 15:59 UTC. Cache hit: $0.028/1M input tokens.'
  },
  {
    key: 'deepseek/deepseek-v3.1-terminus',
    name: 'DeepSeek V3.1 Terminus',
    color: 'bg-cyan-500',
    premium: false,
    cost: { input: '$0.27', output: '$1.00' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '1-2 min' },
    isReasoning: true,
    apiModelName: 'deepseek/deepseek-v3.1-terminus',
    modelType: 'openrouter',
    contextWindow: 163840,
    maxOutputTokens: 32000,
    releaseDate: "2025-09",
    supportsStructuredOutput: true
  },

  {
    key: 'qwen/qwen-plus-2025-07-28:thinking',
    name: 'Qwen: Qwen Plus 0728 (thinking)',
    color: 'bg-orange-700',
    premium: true,
    cost: { input: '$0.40 - $1.20', output: '$4.00 - $12.00' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'slow', estimate: '2-4 min' },
    isReasoning: true,
    apiModelName: 'qwen/qwen-plus-2025-07-28:thinking',
    modelType: 'openrouter',
    contextWindow: 1000000,
    maxOutputTokens: 32800,
    releaseDate: "2025-07",
    supportsStructuredOutput: false // Thinking models often have issues with JSON mode
  },

  // OpenRouter Models (unique models not available through direct APIs)
  {
    key: 'meta-llama/llama-3.3-70b-instruct',
    name: 'Llama 3.3 70B Instruct',
    color: 'bg-orange-600',
    premium: false,
    cost: { input: '$0.59', output: '$0.79' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '1-2 min' },
    isReasoning: false,
    apiModelName: 'meta-llama/llama-3.3-70b-instruct',
    modelType: 'openrouter',
    contextWindow: 128000,
    maxOutputTokens: 32000,
    releaseDate: "2024-06"
  },
  

  {
    key: 'nousresearch/hermes-4-70b',
    name: 'NousResearch Hermes 4 70B',
    color: 'bg-blue-600',
    premium: false,
    cost: { input: '$0.07', output: '$0.35' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '30-60 sec' },
    isReasoning: true,
    apiModelName: 'nousresearch/hermes-4-70b',
    modelType: 'openrouter',
    contextWindow: 32768,
    maxOutputTokens: 25000,
  },
  
  {
    key: 'mistralai/mistral-large-2512',
    name: 'Mistral Large 2512',
    color: 'bg-purple-600',
    premium: true,
    cost: { input: '$0.50', output: '$1.50' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '1-2 min' },
    isReasoning: true,
    apiModelName: 'mistralai/mistral-large-2512',
    modelType: 'openrouter',
    contextWindow: 262144,
    releaseDate: "2025-12"
  },
  {
    key: 'mistralai/ministral-14b-2512',
    name: 'Ministral 14B 2512',
    color: 'bg-purple-500',
    premium: false,
    cost: { input: '$0.20', output: '$0.20' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '30-60 sec' },
    isReasoning: false,
    apiModelName: 'mistralai/ministral-14b-2512',
    modelType: 'openrouter',
    contextWindow: 262144,
    releaseDate: "2025-12"
  },
  {
    key: 'mistralai/ministral-8b-2512',
    name: 'Ministral 8B 2512',
    color: 'bg-purple-400',
    premium: false,
    cost: { input: '$0.15', output: '$0.15' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'fast', estimate: '<30 sec' },
    isReasoning: false,
    apiModelName: 'mistralai/ministral-8b-2512',
    modelType: 'openrouter',
    contextWindow: 262144,
    releaseDate: "2025-12"
  },
  {
    key: 'mistralai/ministral-3b-2512',
    name: 'Ministral 3B 2512',
    color: 'bg-purple-300',
    premium: false,
    cost: { input: '$0.10', output: '$0.10' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'fast', estimate: '<30 sec' },
    isReasoning: false,
    apiModelName: 'mistralai/ministral-3b-2512',
    modelType: 'openrouter',
    contextWindow: 262144,
    releaseDate: "2025-12"
  },
  {
    key: 'mistralai/devstral-2512',
    name: 'Devstral 2512',
    color: 'bg-purple-700',
    premium: false,
    cost: { input: '$0.15', output: '$0.60' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '30-60 sec' },
    isReasoning: false,
    apiModelName: 'mistralai/devstral-2512',
    modelType: 'openrouter',
    contextWindow: 262144,
    maxOutputTokens: 32000,
    releaseDate: "2025-12-09"
  },
  {
    key: 'mistralai/devstral-2512:free',
    name: 'Devstral 2512 (Free)',
    color: 'bg-purple-200',
    premium: false,
    cost: { input: '$0.00', output: '$0.00' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '30-60 sec' },
    isReasoning: false,
    apiModelName: 'mistralai/devstral-2512:free',
    modelType: 'openrouter',
    contextWindow: 262144,
    maxOutputTokens: 32000,
    releaseDate: "2025-12-09",
    notes: 'Free version of Devstral 2512 via OpenRouter'
  },
  {
    key: 'deepseek/deepseek-chat-v3.1',
    name: 'DeepSeek Chat v3.1',
    color: 'bg-cyan-700',
    premium: false,
    cost: { input: '$0.27', output: '$1.10' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '30-90 sec' },
    isReasoning: true,
    apiModelName: 'deepseek/deepseek-chat-v3.1',
    modelType: 'openrouter',
    contextWindow: 256000
  },
  {
    key: 'deepseek/deepseek-v3.2',
    name: 'DeepSeek v3.2 Reasoner',
    color: 'bg-cyan-900',
    premium: true,
    cost: { input: '$0.28', output: '$0.42' },
    supportsTemperature: false,
    provider: 'OpenRouter',
    responseTime: { speed: 'slow', estimate: '5-10 min' },
    isReasoning: true,
    apiModelName: 'deepseek/deepseek-v3.2',
    modelType: 'openrouter',
    contextWindow: 128000,
    maxOutputTokens: 64000,
    releaseDate: "2025-12",
    notes: 'DeepSeek-V3.2 thinking mode via OpenRouter. Returns reasoning_content field with chain-of-thought.'
  },
  {
    key: 'allenai/olmo-3-7b-think',
    name: 'OLMo-3 7B Think',
    color: 'bg-blue-500',
    premium: false,
    cost: { input: '$0.12', output: '$0.20' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '30-60 sec' },
    isReasoning: true,
    apiModelName: 'allenai/olmo-3-7b-think',
    modelType: 'openrouter',
    contextWindow: 66000,
    releaseDate: "2025-12",
    notes: 'OLMo-3 7B thinking model from AllenAI via OpenRouter.'
  },
  {
    key: 'allenai/olmo-3-32b-think:free',
    name: 'OLMo-3 32B Think (Free)',
    color: 'bg-blue-400',
    premium: false,
    cost: { input: '$0.00', output: '$0.00' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '1-2 min' },
    isReasoning: true,
    apiModelName: 'allenai/olmo-3-32b-think:free',
    modelType: 'openrouter',
    contextWindow: 66000,
    releaseDate: "2025-12",
    notes: 'Free OLMo-3 32B thinking model from AllenAI via OpenRouter.'
  },

  // New OpenRouter Models - August 2025
  {
    key: 'openai/gpt-oss-120b',
    name: 'OpenAI GPT-OSS 120B',
    color: 'bg-blue-400',
    premium: false,
    cost: { input: '$0.05', output: '$0.45' },
    supportsTemperature: false,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '30-60 sec' },
    isReasoning: true,
    apiModelName: 'openai/gpt-oss-120b',
    modelType: 'openrouter',
  
    contextWindow: 131000,
    maxOutputTokens: 20000
  },
  {
    key: 'mistralai/codestral-2508',
    name: 'Mistral Codestral 2508',
    color: 'bg-orange-400',
    premium: false,
    cost: { input: '$0.30', output: '$0.90' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'fast', estimate: '<30 sec' },
    isReasoning: false,
    apiModelName: 'mistralai/codestral-2508',
    modelType: 'openrouter',
    contextWindow: 256000,
    maxOutputTokens: 8000
  },
  {
    key: 'qwen/qwen3-coder',
    name: 'Qwen3 Coder',
    color: 'bg-red-500',
    premium: false,
    cost: { input: '$0.20', output: '$0.80' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '30-60 sec' },
    isReasoning: false,
    apiModelName: 'qwen/qwen3-coder',
    modelType: 'openrouter',
    contextWindow: 262144,
    maxOutputTokens: 31000
  },
  {
    key: 'moonshotai/kimi-k2-thinking',
    name: 'Moonshot Kimi K2 Thinking',
    color: 'bg-slate-700',
    premium: true,
    cost: { input: '$0.60', output: '$2.50' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'slow', estimate: '2-4 min' },
    isReasoning: true,
    apiModelName: 'moonshotai/kimi-k2-thinking',
    modelType: 'openrouter',
    contextWindow: 262144,
    maxOutputTokens: 31000,
    releaseDate: "2025-11",
  },
  

  {
    key: 'moonshotai/kimi-dev-72b:free',
    name: 'Kimi Dev 72B (Free)',
    color: 'bg-slate-400',
    premium: false,
    cost: { input: '$0.00', output: '$0.00' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '1-2 min' },
    isReasoning: false,
    apiModelName: 'moonshotai/kimi-dev-72b:free',
    modelType: 'openrouter',
    contextWindow: 131072,
    maxOutputTokens: 31000

  },
  {
    key: 'openrouter/gpt-5.1-codex-mini',
    name: 'GPT-5.1 Codex Mini (OpenRouter)',
    color: 'bg-amber-600',
    premium: true,
    cost: { input: '$0.25', output: '$2.00' },
    supportsTemperature: false,
    supportsVision: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '30-60 sec' },
    isReasoning: true,
    apiModelName: 'openai/gpt-5.1-codex-mini',
    modelType: 'openrouter',
    contextWindow: 400000,
    supportsStreaming: true,
    releaseDate: "2025-11",
    notes: 'Sep 30, 2024 knowledge cutoff; reasoning token support.'
  },
  {
    key: 'openai/gpt-5.1',
    name: 'OpenAI: GPT-5.1',
    color: 'bg-sky-700',
    premium: true,
    cost: { input: '$1.25', output: '$10.00' },
    supportsTemperature: true,
    supportsVision: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '30-90 sec' },
    isReasoning: true,
    apiModelName: 'openai/gpt-5.1',
    modelType: 'openrouter',
    contextWindow: 400000,
    maxOutputTokens: 60000,
    releaseDate: "2025-11"
  },
  {
    key: 'openai/gpt-5-nano',
    name: 'OpenAI: GPT-5 Nano',
    color: 'bg-sky-400',
    premium: false,
    cost: { input: '$0.05', output: '$0.40' },
    supportsTemperature: true,
    supportsVision: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'fast', estimate: '<30 sec' },
    isReasoning: true,
    apiModelName: 'openai/gpt-5-nano',
    modelType: 'openrouter',
    contextWindow: 400000,
    maxOutputTokens: 50000,
    releaseDate: "2025-12"
  },
  {
    key: 'openai/gpt-5-mini',
    name: 'OpenAI: GPT-5 Mini',
    color: 'bg-sky-500',
    premium: false,
    cost: { input: '$0.25', output: '$2.00' },
    supportsTemperature: true,
    supportsVision: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '30-60 sec' },
    isReasoning: true,
    apiModelName: 'openai/gpt-5-mini',
    modelType: 'openrouter',
    contextWindow: 400000,
    maxOutputTokens: 60000,
    releaseDate: "2025-12"
  },
  {
    key: 'openai/gpt-5.2',
    name: 'OpenAI: GPT-5.2',
    color: 'bg-sky-600',
    premium: true,
    cost: { input: '$1.75', output: '$14.00' },
    supportsTemperature: true,
    supportsVision: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '30-90 sec' },
    isReasoning: true,
    apiModelName: 'openai/gpt-5.2',
    modelType: 'openrouter',
    contextWindow: 400000,
    maxOutputTokens: 60000,
    releaseDate: "2025-12",
    notes: 'Latest GPT-5 variant with enhanced reasoning. Available via OpenRouter only.'
  },
  // xAI Grok Models (Direct API via Responses API)
  {
    key: 'grok-4',
    name: 'Grok 4',
    color: 'bg-gray-900',
    premium: true,
    cost: { input: '$3.00', output: '$15.00' },
    supportsTemperature: true,
    provider: 'xAI',
    responseTime: { speed: 'slow', estimate: '3-5+ min' },
    isReasoning: true,
    apiModelName: 'grok-4',
    modelType: 'grok',
    contextWindow: 256000,
    maxOutputTokens: 31000,
    releaseDate: "2025-07"
  },
  {
    key: 'grok-4-fast-reasoning',
    name: 'Grok 4 Fast Reasoning',
    color: 'bg-orange-500',
    premium: false,
    cost: { input: '$0.20', output: '$0.50' },
    supportsTemperature: true,
    provider: 'xAI',
    responseTime: { speed: 'moderate', estimate: '1-2 min' },
    isReasoning: true,
    apiModelName: 'grok-4-fast-reasoning',
    modelType: 'grok',
    contextWindow: 128000,
    maxOutputTokens: 30000,
    releaseDate: "2025-09"
  },
  {
    key: 'grok-4-fast-non-reasoning',
    name: 'Grok 4 Fast Non-Reasoning',
    color: 'bg-orange-500',
    premium: false,
    cost: { input: '$0.20', output: '$0.50' },
    supportsTemperature: true,
    provider: 'xAI',
    responseTime: { speed: 'moderate', estimate: '1-2 min' },
    isReasoning: false,
    apiModelName: 'grok-4-fast-non-reasoning',
    modelType: 'grok',
    contextWindow: 128000,
    maxOutputTokens: 30000,
    releaseDate: "2025-09"
  },
  // xAI Grok-3 Models (via OpenRouter - use Chat Completions API)
  {
    key: 'x-ai/grok-3',
    name: 'Grok 3',
    color: 'bg-gray-600',
    premium: true,
    cost: { input: '$3.00', output: '$15.00' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'slow', estimate: '3-5+ min' },
    isReasoning: true,
    apiModelName: 'x-ai/grok-3',
    modelType: 'openrouter',
    contextWindow: 256000,
    maxOutputTokens: 31000,
    releaseDate: "2024-11"
  },
  {
    key: 'x-ai/grok-3-mini',
    name: 'Grok 3 Mini',
    color: 'bg-gray-500',
    premium: false,
    cost: { input: '$0.30', output: '$0.50' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '1-2 min' },
    isReasoning: true,
    apiModelName: 'x-ai/grok-3-mini',
    modelType: 'openrouter',
    contextWindow: 256000,
    maxOutputTokens: 31000,
    releaseDate: "2024-11"
  },
  {
    key: 'x-ai/grok-code-fast-1',
    name: 'Grok Code Fast 1',
    color: 'bg-gray-400',
    premium: false,
    cost: { input: '$0.20', output: '$1.50' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'fast', estimate: '<30 sec' },
    isReasoning: false,
    apiModelName: 'x-ai/grok-code-fast-1',
    modelType: 'openrouter',
    contextWindow: 256000,
    maxOutputTokens: 25000,
    supportsStructuredOutput: false,
    releaseDate: "2025-05"
  },
  {
    key: 'x-ai/grok-3-mini-fast',
    name: 'Grok 3 Mini Fast',
    color: 'bg-gray-300',
    premium: false,
    cost: { input: '$0.20', output: '$0.40' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'fast', estimate: '<30 sec' },
    isReasoning: true,
    apiModelName: 'x-ai/grok-3-mini-fast',
    modelType: 'openrouter',
    contextWindow: 256000,
    maxOutputTokens: 31000,
    releaseDate: "2024-11"
  },

  // Anthropic Models (via OpenRouter)
  {
    key: 'anthropic/claude-opus-4-6',
    name: 'Claude Opus 4.6 (OpenRouter)',
    color: 'bg-indigo-900',
    premium: true,
    cost: { input: '$5.00', output: '$25.00' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'slow', estimate: '2-5 min' },
    isReasoning: true,
    apiModelName: 'anthropic/claude-opus-4-6',
    modelType: 'openrouter',
    contextWindow: 200000,
    maxOutputTokens: 32000,
    releaseDate: "2026-03",
    notes: 'EXPENSIVE: $5/M input, $25/M output. Use sparingly for benchmarking only.'
  },
  {
    key: 'anthropic/claude-sonnet-4-6',
    name: 'Claude Sonnet 4.6 (OpenRouter)',
    color: 'bg-indigo-600',
    premium: true,
    cost: { input: '$3.00', output: '$15.00' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '1-3 min' },
    isReasoning: true,
    apiModelName: 'anthropic/claude-sonnet-4-6',
    modelType: 'openrouter',
    contextWindow: 200000,
    maxOutputTokens: 64000,
    releaseDate: "2026-03"
  },
  {
    key: 'anthropic/claude-haiku-4-6',
    name: 'Claude Haiku 4.6 (OpenRouter)',
    color: 'bg-indigo-300',
    premium: false,
    cost: { input: '$1.00', output: '$5.00' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'fast', estimate: '<30 sec' },
    isReasoning: false,
    apiModelName: 'anthropic/claude-haiku-4-6',
    modelType: 'openrouter',
    contextWindow: 200000,
    maxOutputTokens: 16000,
    releaseDate: "2026-03"
  },
  {
    key: 'anthropic/claude-opus-4.5',
    name: 'Claude Opus 4.5 (OpenRouter)',
    color: 'bg-indigo-900',
    premium: true,
    cost: { input: '$5.00', output: '$25.00' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'slow', estimate: '2-5 min' },
    isReasoning: true,
    apiModelName: 'anthropic/claude-opus-4.5',
    modelType: 'openrouter',
    contextWindow: 200000,
    maxOutputTokens: 16000,
    releaseDate: "2025-11",
    notes: 'EXTREMELY EXPENSIVE: $5/M input, $25/M output. Use sparingly for benchmarking only.'
  },
  {
    key: 'anthropic/claude-sonnet-4-5',
    name: 'Claude Sonnet 4.5 (OpenRouter)',
    color: 'bg-indigo-600',
    premium: true,
    cost: { input: '$3.00', output: '$15.00' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '1-3 min' },
    isReasoning: true,
    apiModelName: 'anthropic/claude-sonnet-4-5',
    modelType: 'openrouter',
    contextWindow: 200000,
    maxOutputTokens: 64000,
    releaseDate: "2025-09"
  },
  {
    key: 'anthropic/claude-haiku-4.5',
    name: 'Claude Haiku 4.5 (OpenRouter)',
    color: 'bg-indigo-300',
    premium: false,
    cost: { input: '$1.00', output: '$5.00' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'fast', estimate: '<30 sec' },
    isReasoning: true,
    apiModelName: 'anthropic/claude-haiku-4.5',
    modelType: 'openrouter',
    contextWindow: 200000,
    maxOutputTokens: 116000,
    releaseDate: "2025-10"
  },

  {
    key: 'nvidia/nemotron-nano-9b-v2',
    name: 'Nemotron Nano 9B V2',
    color: 'bg-yellow-600',
    premium: false,
    cost: { input: '$0.00', output: '$0.00' },
    supportsTemperature: false,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '1-2 min' },
    isReasoning: false,
    apiModelName: 'nvidia/nemotron-nano-9b-v2',
    modelType: 'openrouter',
    contextWindow: 128000,
    maxOutputTokens: 124000,
    releaseDate: "2025-09"
  },
  {
    key: 'nvidia/nemotron-nano-12b-v2-vl:free',
    name: 'Nemotron Nano 12B V2 VL (Free)',
    color: 'bg-yellow-500',
    premium: false,
    cost: { input: '$0.00', output: '$0.00' },
    supportsTemperature: false,
    supportsVision: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '1-2 min' },
    isReasoning: false,
    apiModelName: 'nvidia/nemotron-nano-12b-v2-vl:free',
    modelType: 'openrouter',
    contextWindow: 128000,
    maxOutputTokens: 124000,
    releaseDate: "2025-10",
    notes: 'Vision-language variant; zero-cost promotional access.'
  },
  {
    key: 'amazon/nova-premier-v1',
    name: 'Amazon Nova Premier 1.0',
    color: 'bg-amber-600',
    premium: true,
    cost: { input: '$2.50', output: '$12.50' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '30-60 sec' },
    isReasoning: true,
    apiModelName: 'amazon/nova-premier-v1',
    modelType: 'openrouter',
    contextWindow: 1000000,
    maxOutputTokens: 60000,
    releaseDate: "2025-10"
  },

  {
    key: 'minimax/minimax-m2',
    name: 'MiniMax M2',
    color: 'bg-rose-500',
    premium: false,
    cost: { input: '$0.30', output: '$1.20' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '30-60 sec' },
    isReasoning: false,
    apiModelName: 'minimax/minimax-m2',
    modelType: 'openrouter',
    contextWindow: 196608,
    releaseDate: "2025-10"
  },

  {
    key: 'google/gemini-2.5-flash-preview-09-2025',
    name: 'Gemini 2.5 Flash Preview (Sep 2025)',
    color: 'bg-teal-300',
    premium: false,
    cost: { input: '$0.30', output: '$2.50' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'fast', estimate: '<30 sec' },
    isReasoning: true,
    apiModelName: 'google/gemini-2.5-flash-preview-09-2025',
    modelType: 'openrouter',
    contextWindow: 1050000,
    maxOutputTokens: 65500,
    releaseDate: "2025-09"
  },

  {
    key: 'google/gemini-3-pro-preview',
    name: 'Gemini 3 Pro Preview',
    color: 'bg-teal-700',
    premium: true,
    cost: { input: '$2.00 - $4.00', output: '$12.00 - $18.00' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '1-2 min' },
    isReasoning: true,
    apiModelName: 'google/gemini-3-pro-preview',
    modelType: 'openrouter',
    contextWindow: 1048576,
    maxOutputTokens: 65536,
    releaseDate: "2025-11",
    supportsStreaming: false,
    supportsStructuredOutput: false, // Reasoning models have issues with JSON mode enforcement
    notes: 'Reasoning details must be preserved when using multi-turn tool calling. See: https://openrouter.ai/docs/use-cases/reasoning-tokens#preserving-reasoning-blocks'
  },
  // Mirror the Gemini 3 Flash Preview release in the OpenRouter catalog so BYO paths can hit the fast-thinking tier.
  {
    key: 'google/gemini-3-flash-preview',
    name: 'Gemini 3 Flash Preview',
    color: 'bg-teal-500',
    premium: false,
    cost: { input: '$0.50', output: '$3.00' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'fast', estimate: '<30 sec' },
    isReasoning: true,
    apiModelName: 'google/gemini-3-flash-preview',
    modelType: 'openrouter',
    contextWindow: 1048576,
    maxOutputTokens: 65535,
    releaseDate: "2025-12",
    supportsVision: true,
    notes: 'Flash preview alias sourced from the OpenRouter catalog; use this slug when routing through LiteLLM.'
  },

  {
    key: 'z-ai/glm-4.6',
    name: 'GLM 4.6',
    color: 'bg-sky-500',
    premium: false,
    cost: { input: '$0.60', output: '$2.20' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '30-60 sec' },
    isReasoning: true,
    apiModelName: 'z-ai/glm-4.6',
    modelType: 'openrouter',
    contextWindow: 200000,
    maxOutputTokens: 128000,
    releaseDate: "2025-09"
  },

  {
    key: 'x-ai/grok-4.1-fast',
    name: 'Grok 4.1 Fast Reasoning',
    color: 'bg-fuchsia-600',
    premium: false,
    cost: { input: '$0.20', output: '$0.50' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '1-2 min' },
    isReasoning: true,
    apiModelName: 'x-ai/grok-4.1-fast',
    modelType: 'openrouter',
    contextWindow: 2000000,
    maxOutputTokens: 50000,
    releaseDate: "2025-11"
  },

  // Beetree Ensemble Solver
  {
    key: 'beetree-ensemble',
    name: 'Beetree Ensemble',
    color: 'bg-emerald-600',
    premium: false,
    cost: { input: '$2.00', output: '$5.00' },
    supportsTemperature: false,
    provider: 'Beetree',
    responseTime: { speed: 'slow', estimate: '3-8 min' },
    apiModelName: 'beetree-ensemble',
    modelType: 'beetree',
    contextWindow: 1000000,
    maxOutputTokens: 50000,
    releaseDate: "2025-12",
    notes: 'Multi-model ensemble solver with consensus analysis and cost tracking'
  },

  // Free OpenRouter Models - December 2025
  {
    key: 'arcee-ai/trinity-mini:free',
    name: 'Arcee Trinity Mini (Free)',
    color: 'bg-cyan-400',
    premium: false,
    cost: { input: '$0.00', output: '$0.00' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '30-60 sec' },
    isReasoning: false,
    apiModelName: 'arcee-ai/trinity-mini:free',
    modelType: 'openrouter',
    contextWindow: 131072,
    releaseDate: "2025-12"
  },
  {
    key: 'amazon/nova-2-lite-v1:free',
    name: 'Amazon Nova 2 Lite (Free)',
    color: 'bg-amber-400',
    premium: false,
    cost: { input: '$0.00', output: '$0.00' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'fast', estimate: '<30 sec' },
    isReasoning: false,
    apiModelName: 'amazon/nova-2-lite-v1:free',
    modelType: 'openrouter',
    contextWindow: 1000000,
    releaseDate: "2025-12"
  },

  // Cloaked Models (OpenRouter Arena - identity TBD)
  {
    key: 'kwaipilot/kat-coder-pro:free',
    name: 'Kat Coder Pro (Free)',
    color: 'bg-rose-500',
    premium: false,
    cost: { input: '$0.00', output: '$0.00' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '30-60 sec' },
    isReasoning: true,
    apiModelName: 'kwaipilot/kat-coder-pro:free',
    modelType: 'openrouter',
    contextWindow: 131072,
    releaseDate: "2025-11",
    notes: 'Free KatPilot coder-tier model on OpenRouter arena.'
  },
  {
    key: 'z-ai/glm-4.6v',
    name: 'GLM 4.6V (Vision)',
    color: 'bg-sky-600',
    premium: false,
    cost: { input: '$0.30', output: '$0.90' },
    supportsTemperature: true,
    supportsVision: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '30-60 sec' },
    isReasoning: false,
    apiModelName: 'z-ai/glm-4.6v',
    modelType: 'openrouter',
    contextWindow: 131072,
    maxOutputTokens: 24000,
    releaseDate: "2025-12",
    notes: 'Multimodal model for visual understanding and long-context reasoning. Supports native multimodal function calling, interleaved image-text generation, and UI reconstruction workflows including screenshot-to-HTML synthesis.'
  },

  {
    key: 'nex-agi/deepseek-v3.1-nex-n1:free',
    name: 'Nex AGI: DeepSeek V3.1 Nex N1 (Free)',
    color: 'bg-cyan-600',
    premium: false,
    cost: { input: '$0.00', output: '$0.00' },
    supportsTemperature: true,
    provider: 'OpenRouter',
    responseTime: { speed: 'moderate', estimate: '30-60 sec' },
    isReasoning: false,
    apiModelName: 'nex-agi/deepseek-v3.1-nex-n1:free',
    modelType: 'openrouter',
    contextWindow: 131072,
    releaseDate: "2025-12",
    notes: 'Free DeepSeek V3.1 variant via OpenRouter'
  },

];

// Only build OpenRouter models in Node.js environment (server-side)
const OPENROUTER_MODELS = typeof process !== 'undefined' ? buildOpenRouterModels() : [];

// Final export merges static entries with catalog-driven OpenRouter models (catalog is SoT).
export const MODELS: ModelConfig[] = [
  ...STATIC_MODELS.filter(model => model.provider !== 'OpenRouter'),
  ...OPENROUTER_MODELS
];
